import { useEffect, useState } from "react";
import {
  LogOut,
  Calendar,
  FileText,
  ClipboardList,
  CheckCircle2,
  Circle,
  Send,
  MessageCircle,
} from "lucide-react";
import { useContent } from "@/content/ContentContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-foreground/15 bg-background px-3 py-2.5 text-sm text-foreground outline-none transition-colors duration-300 placeholder:text-muted-foreground/50 focus:border-primary";
const labelCls =
  "mb-1.5 block font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground";

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Etwas ist schiefgelaufen. Bitte noch mal versuchen.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const statusMeta = {
  anfrage: { label: "Anfrage", cls: "border-foreground/25 text-muted-foreground" },
  in_arbeit: { label: "In Arbeit", cls: "border-primary text-primary" },
  pausiert: { label: "Pausiert", cls: "border-foreground/25 text-muted-foreground" },
  fertig: { label: "Fertig", cls: "border-green-500/60 text-green-400" },
};

const kindMeta = {
  bericht: { label: "Bericht", Icon: ClipboardList },
  termin: { label: "Termin", Icon: Calendar },
  dokument: { label: "Dokument", Icon: FileText },
};

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("de-DE");
};

export default function Portal() {
  const content = useContent();
  const johnWhatsApp = `https://wa.me/${content.contact.whatsapp}?text=${encodeURIComponent(content.contact.whatsapp_text)}`;
  const [token, setToken] = useState(() => localStorage.getItem("rundum_portal_token"));
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(!!token);
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [error, setError] = useState("");
  const [projects, setProjects] = useState(null);
  const [messages, setMessages] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [msgText, setMsgText] = useState({});
  const [msgSent, setMsgSent] = useState({});
  const [phoneInput, setPhoneInput] = useState("");
  const [phoneSaved, setPhoneSaved] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/auth/me`, { headers: authHeaders })
      .then((r) => {
        if (!r.ok) throw new Error("unauthorized");
        return r.json();
      })
      .then((u) => {
        setUser(u);
        setPhoneInput(u.phone || "");
        setChecking(false);
      })
      .catch(() => {
        localStorage.removeItem("rundum_portal_token");
        setToken(null);
        setChecking(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProjects = () =>
    fetch(`${API}/portal/projects`, { headers: authHeaders })
      .then((r) => (r.ok ? r.json() : { projects: [] }))
      .then((d) => setProjects(d.projects))
      .catch(() => setProjects([]));

  const loadMessages = () =>
    fetch(`${API}/portal/messages`, { headers: authHeaders })
      .then((r) => (r.ok ? r.json() : { messages: [] }))
      .then((d) => setMessages(d.messages))
      .catch(() => setMessages([]));

  useEffect(() => {
    if (user) {
      loadProjects();
      loadMessages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const submitAuth = async (e) => {
    e.preventDefault();
    setError("");
    const url = mode === "login" ? `${API}/auth/login` : `${API}/auth/register`;
    const body =
      mode === "login"
        ? { username: form.email, password: form.password }
        : { name: form.name, email: form.email, password: form.password, phone: form.phone };
    try {
      const r = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(formatApiErrorDetail(data.detail));
      localStorage.setItem("rundum_portal_token", data.access_token);
      setToken(data.access_token);
      setUser(data.user);
      setPhoneInput(data.user.phone || "");
    } catch (err) {
      setError(err.message);
    }
  };

  const sendMessage = async (key, projectId = "") => {
    const text = (msgText[key] || "").trim();
    if (text.length < 3) return;
    const r = await fetch(`${API}/portal/messages`, {
      method: "POST",
      headers: { ...authHeaders, "Content-Type": "application/json" },
      body: JSON.stringify({ project_id: projectId, text }),
    });
    if (r.ok) {
      setMsgText((s) => ({ ...s, [key]: "" }));
      setMsgSent((s) => ({ ...s, [key]: true }));
      setTimeout(() => setMsgSent((s) => ({ ...s, [key]: false })), 3000);
      loadMessages();
    }
  };

  const logout = () => {
    localStorage.removeItem("rundum_portal_token");
    setToken(null);
    setUser(null);
    setProjects(null);
    setMessages(null);
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">Lade …</p>
      </div>
    );
  }

  if (!token || !user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6 py-16">
        <form
          onSubmit={submitAuth}
          data-testid="portal-auth-form"
          className="w-full max-w-sm space-y-5 border border-foreground/10 bg-card p-8"
        >
          <img src="/rundum-logo.png" alt="Rundum Logo" className="h-10 w-auto object-contain object-left" />
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">Kundenportal</p>
          <div className="flex gap-2">
            {[
              { id: "login", label: "Einloggen" },
              { id: "register", label: "Registrieren" },
            ].map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => setMode(m.id)}
                data-testid={`portal-mode-${m.id}`}
                className={`flex-1 border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                  mode === m.id
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-foreground/15 text-muted-foreground hover:border-primary hover:text-primary"
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
          {mode === "register" && (
            <>
              <div>
                <label htmlFor="portal-name" className={labelCls}>Dein Name</label>
                <input
                  id="portal-name"
                  data-testid="portal-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="portal-phone" className={labelCls}>Handynummer (freiwillig, für WhatsApp)</label>
                <input
                  id="portal-phone"
                  data-testid="portal-phone-input"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="z. B. 0171 1234567"
                  className={inputCls}
                />
              </div>
            </>
          )}
          <div>
            <label htmlFor="portal-email" className={labelCls}>E-Mail</label>
            <input
              id="portal-email"
              data-testid="portal-email-input"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              className={inputCls}
              autoComplete="email"
            />
          </div>
          <div>
            <label htmlFor="portal-password" className={labelCls}>
              Passwort{mode === "register" ? " (mind. 8 Zeichen)" : ""}
            </label>
            <input
              id="portal-password"
              data-testid="portal-password-input"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
              minLength={mode === "register" ? 8 : 1}
              className={inputCls}
              autoComplete={mode === "login" ? "current-password" : "new-password"}
            />
          </div>
          {error && (
            <p className="text-sm text-primary" data-testid="portal-auth-error">{error}</p>
          )}
          <button
            type="submit"
            data-testid="portal-auth-submit"
            className="w-full bg-primary px-6 py-3.5 font-heading text-sm font-bold uppercase tracking-wider text-primary-foreground transition-colors duration-300 hover:bg-foreground hover:text-background"
          >
            {mode === "login" ? "Einloggen" : "Konto erstellen"}
          </button>
          <a
            href="/"
            className="block text-center font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground transition-colors duration-300 hover:text-primary"
          >
            Zur Website
          </a>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="portal-dashboard">
      <header className="sticky top-0 z-40 border-b border-foreground/10 bg-background/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <img src="/rundum-logo.png" alt="Rundum Logo" className="h-8 w-auto object-contain object-left" />
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Kundenportal</span>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={johnWhatsApp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="portal-whatsapp-john"
              className="inline-flex items-center gap-2 border border-foreground/15 px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground transition-colors duration-300 hover:border-primary hover:text-primary"
            >
              <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
            </a>
            <button
              onClick={logout}
              data-testid="portal-logout-button"
              className="inline-flex items-center gap-2 border border-foreground/15 px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground transition-colors duration-300 hover:border-primary hover:text-primary"
            >
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-6 py-12">
        <h1 className="font-heading text-3xl font-black tracking-tight text-foreground md:text-4xl" data-testid="portal-welcome">
          Moin, {user.name || "du"}!
        </h1>
        <p className="mt-3 max-w-xl text-sm text-muted-foreground md:text-base">
          Hier siehst du, wo dein Projekt gerade steht, was als Nächstes ansteht – und du erreichst mich direkt.
        </p>

        <div className="mt-8 flex flex-wrap items-end gap-3 border border-foreground/10 bg-card p-5" data-testid="portal-phone-card">
          <div className="min-w-56 flex-1">
            <label htmlFor="portal-phone-update" className={labelCls}>
              Deine Handynummer (für WhatsApp & schnelle Rückfragen)
            </label>
            <input
              id="portal-phone-update"
              data-testid="portal-phone-update-input"
              type="tel"
              value={phoneInput}
              onChange={(e) => setPhoneInput(e.target.value)}
              placeholder="z. B. 0171 1234567"
              className={inputCls}
            />
          </div>
          <button
            onClick={async () => {
              const phone = phoneInput.trim();
              const r = await fetch(`${API}/portal/profile`, {
                method: "PUT",
                headers: { ...authHeaders, "Content-Type": "application/json" },
                body: JSON.stringify({ phone }),
              });
              if (r.ok) {
                setUser({ ...user, phone });
                setPhoneSaved(true);
                setTimeout(() => setPhoneSaved(false), 2500);
              }
            }}
            data-testid="portal-phone-save"
            className="inline-flex items-center gap-2 bg-primary px-5 py-2.5 font-heading text-xs font-bold uppercase tracking-wider text-primary-foreground transition-colors duration-300 hover:bg-foreground hover:text-background"
          >
            {phoneSaved ? "Gespeichert!" : "Speichern"}
          </button>
        </div>

        <div className="mt-10 space-y-6">
          {projects === null ? (
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">Lade Projekte …</p>
          ) : projects.length === 0 ? (
            <div className="border border-foreground/10 bg-card p-8" data-testid="portal-no-projects">
              <p className="font-heading text-xl font-bold text-foreground">Noch kein Projekt angelegt.</p>
              <p className="mt-2 text-sm text-muted-foreground">
                Sobald wir zusammen starten, siehst du hier Status, Zeitleiste und Berichte. Bis dahin: Schreib mir unten einfach direkt.
              </p>
            </div>
          ) : (
            projects.map((p) => {
              const steps = p.steps || [];
              const updates = p.updates || [];
              const done = steps.filter((s) => s.done).length;
              const pct = steps.length ? Math.round((done / steps.length) * 100) : 0;
              const st = statusMeta[p.status] || statusMeta.in_arbeit;
              const open = openId === p.id;
              return (
                <div key={p.id} className="border border-foreground/10 bg-card" data-testid={`project-card-${p.id}`}>
                  <div className="p-6 md:p-8">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <h2 className="font-heading text-xl font-black tracking-tight text-foreground md:text-2xl">{p.title}</h2>
                        {p.address && (
                          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{p.address}</p>
                        )}
                      </div>
                      <span className={`border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] ${st.cls}`} data-testid={`project-status-${p.id}`}>
                        {st.label}
                      </span>
                    </div>
                    {p.description && (
                      <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted-foreground">{p.description}</p>
                    )}

                    {steps.length > 0 && (
                      <div className="mt-6">
                        <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                          <span>{done} von {steps.length} Schritten erledigt</span>
                          <span className="text-primary">{pct} %</span>
                        </div>
                        <div className="mt-2 h-1.5 w-full bg-foreground/10">
                          <div className="h-full bg-primary transition-[width] duration-700" style={{ width: `${pct}%` }} data-testid={`project-progress-${p.id}`} />
                        </div>
                      </div>
                    )}

                    {p.next_appointment && (
                      <div className="mt-6 flex items-center gap-3 border border-primary/40 bg-primary/10 px-4 py-3" data-testid={`project-next-${p.id}`}>
                        <Calendar className="h-4 w-4 shrink-0 text-primary" />
                        <p className="text-sm text-foreground"><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-primary">Nächster Termin · </span>{p.next_appointment}</p>
                      </div>
                    )}

                    <button
                      onClick={() => setOpenId(open ? null : p.id)}
                      data-testid={`project-toggle-${p.id}`}
                      className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground transition-colors duration-300 hover:text-primary"
                    >
                      {open ? "Details ausblenden ↑" : "Zeitleiste, Berichte & Nachricht ansehen ↓"}
                    </button>
                  </div>

                  {open && (
                    <div className="grid grid-cols-1 gap-10 border-t border-foreground/10 p-6 md:grid-cols-2 md:p-8">
                      <div>
                        <p className="mb-5 font-mono text-[10px] uppercase tracking-[0.25em] text-primary">Zeitleiste</p>
                        {steps.length === 0 ? (
                          <p className="text-sm text-muted-foreground">Noch keine Schritte eingetragen.</p>
                        ) : (
                          <ul className="space-y-0">
                            {steps.map((s, i) => (
                              <li key={i} className="relative flex gap-4 pb-6 last:pb-0">
                                {i < steps.length - 1 && (
                                  <span className="absolute left-[11px] top-6 h-full w-px bg-foreground/10" />
                                )}
                                {s.done ? (
                                  <CheckCircle2 className="h-[22px] w-[22px] shrink-0 text-primary" />
                                ) : (
                                  <Circle className="h-[22px] w-[22px] shrink-0 text-muted-foreground/50" />
                                )}
                                <div>
                                  <p className={`text-sm ${s.done ? "text-foreground" : "text-muted-foreground"}`}>{s.title}</p>
                                  {!s.done && i === steps.findIndex((x) => !x.done) && (
                                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-primary">Kommt als Nächstes</p>
                                  )}
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}

                        <p className="mb-5 mt-10 font-mono text-[10px] uppercase tracking-[0.25em] text-primary">Berichte & Dokumente</p>
                        {updates.length === 0 ? (
                          <p className="text-sm text-muted-foreground">Noch keine Einträge.</p>
                        ) : (
                          <ul className="space-y-4">
                            {updates.map((u, i) => {
                              const meta = kindMeta[u.kind] || kindMeta.bericht;
                              const photos = u.photos || (u.photo ? [u.photo] : []);
                              return (
                                <li key={i} className="flex gap-3 border border-foreground/10 p-4">
                                  <meta.Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                                  <div className="min-w-0 flex-1">
                                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                      {meta.label}{u.date ? ` · ${formatDate(u.date)}` : ""}
                                    </p>
                                    <p className="mt-1 text-sm text-foreground">{u.text}</p>
                                    {photos.length > 0 && (
                                      <div className="mt-3 grid grid-cols-2 gap-2">
                                        {photos.map((ph, pi) => (
                                          <a key={pi} href={`${API}/files/${ph}?auth=${token}`} target="_blank" rel="noopener noreferrer">
                                            <img
                                              src={`${API}/files/${ph}?auth=${token}`}
                                              alt={`Baustellen-Foto ${pi + 1}`}
                                              className="h-28 w-full border border-foreground/10 object-cover transition-opacity duration-300 hover:opacity-80"
                                              data-testid={`update-photo-${p.id}-${i}-${pi}`}
                                            />
                                          </a>
                                        ))}
                                      </div>
                                    )}
                                    {u.link && (
                                      <a href={u.link} target="_blank" rel="noopener noreferrer" className="mt-1 inline-block text-xs text-primary underline underline-offset-2">
                                        Öffnen
                                      </a>
                                    )}
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>

                      <div>
                        <p className="mb-5 font-mono text-[10px] uppercase tracking-[0.25em] text-primary">Nachricht zu diesem Projekt</p>
                        <textarea
                          rows={5}
                          value={msgText[p.id] || ""}
                          onChange={(e) => setMsgText((s) => ({ ...s, [p.id]: e.target.value }))}
                          placeholder="Rückfrage, Änderungswunsch, neuer Auftrag – schreib einfach drauflos …"
                          className={`${inputCls} resize-y`}
                          data-testid={`project-message-input-${p.id}`}
                        />
                        <button
                          onClick={() => sendMessage(p.id, p.id)}
                          data-testid={`project-message-send-${p.id}`}
                          className="mt-3 inline-flex items-center gap-2 bg-primary px-5 py-3 font-heading text-xs font-bold uppercase tracking-wider text-primary-foreground transition-colors duration-300 hover:bg-foreground hover:text-background"
                        >
                          <Send className="h-3.5 w-3.5" />
                          {msgSent[p.id] ? "Verschickt!" : "Abschicken"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}

          <div className="border border-foreground/10 bg-card p-6 md:p-8" data-testid="portal-general-message">
            <p className="font-heading text-lg font-bold text-foreground">Rückfrage oder neuer Auftrag?</p>
            <p className="mt-1 text-sm text-muted-foreground">Direkter Draht zu mir – ohne Projektbezug.</p>
            <textarea
              rows={4}
              value={msgText.allgemein || ""}
              onChange={(e) => setMsgText((s) => ({ ...s, allgemein: e.target.value }))}
              placeholder="Worum geht's?"
              className={`${inputCls} mt-4 resize-y`}
              data-testid="portal-general-message-input"
            />
            <button
              onClick={() => sendMessage("allgemein")}
              data-testid="portal-general-message-send"
              className="mt-3 inline-flex items-center gap-2 bg-primary px-5 py-3 font-heading text-xs font-bold uppercase tracking-wider text-primary-foreground transition-colors duration-300 hover:bg-foreground hover:text-background"
            >
              <Send className="h-3.5 w-3.5" />
              {msgSent.allgemein ? "Verschickt!" : "Abschicken"}
            </button>
          </div>

          {messages && messages.length > 0 && (
            <div data-testid="portal-my-messages">
              <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Deine bisherigen Nachrichten</p>
              <ul className="space-y-3">
                {messages.map((m) => (
                  <li key={m.id} className="border border-foreground/10 p-4 text-sm text-muted-foreground">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-primary">{formatDate(m.created_at)}</span>
                    <p className="mt-1 whitespace-pre-line">{m.text}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
